import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  ContextService,
  LabelsService,
  Language,
  provideAiguillage,
  provideLocaleId,
  provideOverlayContainer,
  provideTypology,
  withApimInterceptors,
} from '@bcef/ia-shared';
import {
  DATA_LAYER_PROFILE_TOKEN,
  DATA_LAYER_SELECTORS_TOKEN,
  provideDataLayer,
} from '@difa/ia-data-layer';
import { provideTypologyFileUrl } from '@difa/ia-typology-files';
import { provideJsonConvert } from '@difa/ia-utils';
import { WA_WINDOW } from '@ng-web-apis/common';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore, Store } from '@ngrx/store';
import { provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';

import { environment } from '@environments/environment';
import { PersonalInformationFacade } from '@features/termination/components/personal-information/personal-information.facade';
import { SummaryFacade } from '@features/termination/components/summary/summary.facade';
import { TerminationInformationFacade } from '@features/termination/components/termination-information/termination-information.facade';
import { CustomWindowResilContrats } from '@features/termination/interfaces';
import {
  AndroidWebviewService,
  BrowserWebviewService,
  IosWebviewService,
  WebviewService,
} from '@features/termination/services/webview';
import { list, selectTerminationContract } from '@features/termination/store';
import { configFeature } from '@features/termination/store/reducers/config/config.reducer';
import { terminationFeature } from '@features/termination/store/reducers/termination/termination.reducers';
import { TerminationFacade } from '@features/termination/termination.facade';
import { DataLayerSelectors } from '@features/termination/types/data-layer.type';

import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    provideAnimations(),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideState(configFeature),
    provideState(terminationFeature),
    provideEffects([...list]),
    provideStore(
      {
        router: routerReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      },
    ),
    environment.providers,
    TerminationFacade,
    DatePipe,
    SummaryFacade,
    TerminationInformationFacade,
    PersonalInformationFacade,

    provideRouterStore({
      stateKey: 'router',
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      // turn this to `true` if you want to enable 'fondu enchaîné' effect when navigating.
      // withViewTransitions({ skipInitialTransition: true }),
    ),
    provideAiguillage(),
    provideTranslateService({
      defaultLanguage: Language.FR,
      fallbackLang: Language.FR,
      loader: provideTranslateLoader(LabelsService),
    }),
    provideLocaleId(),
    provideTypology(),
    provideTypologyFileUrl(() => {
      const context = inject(ContextService);
      return `/apps/ia-resil-contrats/labels/ia-resil-contrats-${context.language}-${context.typology}-V1.json`;
    }),
    provideDataLayer({
      serviceName: 'starter-kit',
      prefix: 'dataLayer',
      // turn this property to `true` if you want to log warnings in the
      // console if some of your DataLayer objects
      // are formatted properly (only in development mode)
      // it needs the `prefix` property to be set in order to work
      checkDataLayerObjectsIntegrity: false,
    }),
    provideAppInitializer(() => inject(LabelsService).getTranslation()),
    provideHttpClient(withInterceptorsFromDi(), withApimInterceptors()),
    provideJsonConvert(),
    provideOverlayContainer(),
    {
      provide: WebviewService,
      useFactory: (
        window: CustomWindowResilContrats,
      ): AndroidWebviewService | IosWebviewService | BrowserWebviewService => {
        if (typeof window.BnpApp !== 'undefined' && window.BnpApp !== null) {
          return new AndroidWebviewService();
        }

        if (typeof window.webkit !== 'undefined' && window.webkit !== null) {
          return new IosWebviewService();
        }

        return new BrowserWebviewService();
      },
      deps: [WA_WINDOW],
    },
    { provide: DATA_LAYER_PROFILE_TOKEN, useValue: 'resiliationContrat' },
    {
      provide: DATA_LAYER_SELECTORS_TOKEN,
      useFactory: (store: Store): DataLayerSelectors => ({
        selectTerminationContract: store.select(selectTerminationContract),
      }),
      deps: [Store],
    },
  ],
};
